import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { getSafetyProjects, getSafetyBasicInfo, getSafetyProjectYears, getSafetyProjectsOptimized } from '../../../api/safetyAssessApi';
import SafetyProjectModal from './steps/SafetyProjectModal';
import '../Safety.css';

const renderNamePositionBadge = (fullName, isManager = false) => {
  if (!fullName || fullName === '-') return '-';
  const cleanName = fullName.replace(/\s*\/?\(?관리감독자\)?/g, '').replace(/\(.*?\)/g, '').trim();
  const parts = cleanName.split(/\s+/);
  const name = parts[0] || '';
  const pos = parts.slice(1).join(' ') || '';
  const displayText = pos ? `${name} / ${pos}` : name;
  const iconClass = isManager ? 'fas fa-user-check' : 'fas fa-user';
  return (
    <span className="badge bg-light text-dark border px-2 py-1.5 fw-semibold" style={{ fontSize: '0.8rem', borderColor: '#cbd5e1', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>
      <i className={`${iconClass} me-1 text-secondary`} style={{ fontSize: '0.85rem' }}></i>
      {displayText}
    </span>
  );
};



function SafetyProjectList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);

  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(window.location.search).get('status') || '');
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const s = new URLSearchParams(window.location.search).get('status') || '';
    return (s === 'completed' || s.includes('canceled') || s.includes('deleted')) ? 'list' : 'card';
  });
  const [projects, setProjects] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [allProjects, setAllProjects] = useState([]);
  const [stats, setStats] = useState({ totalAssessments: 0, totalHazards: 0, actionRate: 0.0, highRiskImmediate: 0 });
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null); // null, 'create', 'edit', 'view'
  const [modalData, setModalData] = useState(null);

  const [filterType, setFilterType] = useState('ALL');
  const [filterYear, setFilterYear] = useState(() => new Date().getFullYear().toString());
  const [availableYears, setAvailableYears] = useState([]);
  const [searchType, setSearchType] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // 페이징
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = viewMode === 'card' ? 8 : 10;


  // 권한 제어: 사업장 안전관리자 ID (마운트 시 1회 조회)
  const [siteManagerId, setSiteManagerId] = useState('');

  // DB에 존재하는 프로젝트 연도 목록 조회
  useEffect(() => {
    const fetchYears = async () => {
      if (!token) return;
      try {
        const years = await getSafetyProjectYears(token);
        if (years && years.length > 0) {
          setAvailableYears(years);
          const currentYear = new Date().getFullYear().toString();
          // DB 연도 목록에 현재 연도가 없으면, 가장 최근 연도(배열 첫번째)를 기본 선택
          if (!years.includes(parseInt(currentYear, 10)) && !years.includes(currentYear)) {
            setFilterYear(years[0].toString());
          }
        }
      } catch (err) {
        console.error('fetchYears error:', err);
      }
    };
    fetchYears();
  }, [token]);

  useEffect(() => {
    const fetchSiteManager = async () => {
      try {
        const basicInfo = await getSafetyBasicInfo(token);
        if (basicInfo?.managerId) setSiteManagerId(basicInfo.managerId);
      } catch (e) { /* 무시 */ }
    };
    if (token) fetchSiteManager();
  }, [token]);

  // 프로젝트 신규 등록 권한: admin 또는 사업장 안전관리자
  const currentUserId = user?.userid || user?.userId;
  const canCreateProject = user?.role === 'admin' || (siteManagerId && currentUserId === siteManagerId);

  // 프로젝트 상세 모달 수정 권한: admin, 기안자(write_id), 총괄관리자(managerId), 사업장 안전관리자(siteManagerId)
  const hasProjectPermission = useCallback((proj) => {
    if (!user || !proj) return false;
    const currentUserId = user.userid || user.userId;
    if (user.role === 'admin') return true;
    if (currentUserId === proj.writeId) return true;
    if (currentUserId === proj.managerId) return true;
    if (siteManagerId && currentUserId === siteManagerId) return true;
    return false;
  }, [user, siteManagerId]);



  // 전체 프로젝트(탭 카운트용)
  const fetchAllProjects = useCallback(async () => {
    if (!token) return;
    try {
      const params = {};
      if (filterYear !== 'ALL') params.year = filterYear;
      const data = await getSafetyProjects(token, params);
      setAllProjects(data || []);
    } catch (err) {
      console.error(err);
    }
  }, [token, filterYear]);

  // URL의 status 파라미터가 변경되면 statusFilter 상태에 동기화
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const status = urlParams.get('status') || '';
    setStatusFilter(status);
    setCurrentPage(1); // 탭 변경 시 1페이지로
    setProjects([]); // 이전 데이터 즉시 초기화 (잔상 방지)
    setTotalCount(0);
    setLoading(true); // 로딩 상태로 전환

    // 진행중 메뉴는 뱃지(card)가 기본이고, 완료/취소/삭제는 리스트(list)가 기본
    if (status === 'completed' || status.includes('canceled') || status.includes('deleted')) {
      setViewMode('list');
    } else {
      setViewMode('card');
    }
  }, [location.search]);

  // 대시보드 KPI 실시간 통계 조회
  const fetchSummaryStats = useCallback(async () => {
    if (!token) return;
    try {
      // API 한 방 처리로 통합되어 호출 불필요
    } catch (err) {
      console.error('fetchSummaryStats error:', err);
    }
  }, [token]);

  // 서버사이드 페이징/검색 조회
  const fetchProjects = useCallback(async () => {
    if (!token) return;
    try {
      const currentStatus = new URLSearchParams(location.search).get('status') || '';
      const params = { page: currentPage, limit: itemsPerPage };
      if (filterType !== 'ALL') params.filterType = filterType;
      if (filterYear !== 'ALL') params.year = filterYear;
      params.status = (currentStatus === 'in_progress' || !currentStatus) ? (includeCompleted ? 'in_progress,completed' : 'in_progress') : currentStatus;
      if (searchTerm) {
        params.searchType = searchType;
        params.searchTerm = searchTerm;
      }
      const result = await getSafetyProjectsOptimized(token, params);
      
      let fetchedProjects = [];
      let total = 0;
      
      if (result && result.data) {
        fetchedProjects = result.data;
        total = result.total;
        if (result.stats) setStats(result.stats);
      } else {
        fetchedProjects = result || [];
        total = Array.isArray(result) ? result.length : 0;
      }

      // [권한 필터] 취소/삭제된 평가는 권한(admin, 안전관리자 2명, 기안자)이 없는 경우 숨김 처리
      // [권한 필터] 취소/삭제된 평가는 권한(admin, 안전관리자 2명, 기안자)이 없는 경우 숨김 처리
      const filteredProjects = fetchedProjects.filter(p => {
        if (p.status === 'canceled' || p.status === 'deleted') {
          if (!hasProjectPermission(p)) return false;
        }
        // 백엔드에서 페이징과 함께 필터링되어 넘어오므로 프론트단 연도 필터 로직은 제거
        return true;
      });
      
      // 숨겨진 개수만큼 전체 카운트(total)에서 차감하여 UI(페이징) 오차 보정
      const diff = fetchedProjects.length - filteredProjects.length;

      setProjects(filteredProjects);
      setTotalCount(total - diff > 0 ? total - diff : 0);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, filterType, filterYear, searchType, searchTerm, includeCompleted, itemsPerPage, location.search, hasProjectPermission]);

  useEffect(() => { fetchAllProjects(); }, [fetchAllProjects]);
  useEffect(() => { fetchSummaryStats(); }, [fetchSummaryStats]);
  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleOpenModal = () => {
    setModalData(null);
    setModalMode('create');
  };

  const handleOpenViewModal = (proj, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setModalData(proj);
    setModalMode('view');
  };


  // 모달에서 저장/상태변경 완료 후 콜백
  const handleModalSaved = async () => {
    await fetchAllProjects();
    await fetchSummaryStats();
    await fetchProjects();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // 탭 카운트용: 검색어 조건 및 현재 완료포함 상태 적용
  const filteredForCount = allProjects.filter(p => {
    // 0. 취소/삭제 상태 프로젝트 권한 필터링 (권한이 없으면 탭 건수 카운트 및 전체 탭 목록에서도 제외)
    if ((p.status === 'canceled' || p.status === 'deleted') && !hasProjectPermission(p)) {
      return false;
    }

    // 1. 상태 필터링 적용
    const targetStatus = (statusFilter === 'in_progress' || !statusFilter) ? (includeCompleted ? 'in_progress,completed' : 'in_progress') : statusFilter;
    const allowedStatuses = targetStatus.split(',');
    if (!allowedStatuses.includes(p.status)) return false;

    // 2. 검색 필터링 적용
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    if (searchType === 'ALL') {
      return (p.name || '').toLowerCase().includes(q) ||
        (p.siteName || '').toLowerCase().includes(q) ||
        (p.managerName || '').toLowerCase().includes(q) ||
        (p.writeName || '').toLowerCase().includes(q) ||
        (p.status || '').toLowerCase().includes(q);
    } else if (searchType === 'NAME') return (p.name || '').toLowerCase().includes(q);
    else if (searchType === 'SITE') return (p.siteName || '').toLowerCase().includes(q);
    else if (searchType === 'MANAGER') {
      return (p.managerName || '').toLowerCase().includes(q) ||
        (p.writeName || '').toLowerCase().includes(q);
    } else if (searchType === 'STATUS') return (p.status || '').toLowerCase().includes(q);
    return true;
  });

  if (loading) {
    return (
      <div className="sf-page">
        <div className="sf-loading">
          <div className="sf-spinner"></div>
          <span className="sf-loading-text">위험성 평가 현황 로딩 중...</span>
        </div>
      </div>
    );
  }




  return (
    <div className="sf-page sf-animate" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', background: '#ffffff' }}>
      <style>{`
        .hero-banner { background: linear-gradient(to right, #0f172a 0%, #1e293b 25%, #64748b 100%); border-radius: 12px; padding: 1.5rem 2rem 2.25rem 2rem; color: white; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; position: relative; overflow: hidden; }
        .kpi-wrapper { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; margin-top: -1.25rem; padding: 0 1.5rem; position: relative; z-index: 10; }
        .kpi-box { background: white; border-radius: 12px; padding: 0.85rem 1.25rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid rgba(226, 232, 240, 0.8); }
        .hero-banner p { margin: 0; color: #94a3b8; font-size: 0.85rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .date-picker-wrap .form-control { cursor: pointer !important; padding-right: 32px !important; text-align: center; background-color: #f8fafc !important; border: 1.5px solid var(--sf-border) !important; transition: var(--sf-transition) !important; }
        .date-picker-wrap .form-control:focus { background-color: #ffffff !important; border-color: var(--sf-primary) !important; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12) !important; outline: none !important; }
      `}</style>

      {/* 헤더 카드 */}
      <div className="hero-banner">
        <div style={{ flex: 1, minWidth: 0, paddingRight: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ background: '#334155', color: '#e2e8f0', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600 }}>통합 안전관리</span>
          </div>
          <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: 'white', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
            위험성 평가현황 <span style={{ fontSize: '1rem', fontWeight: 600, color: '#cbd5e1', marginLeft: '4px' }}>{statusFilter === 'completed' ? '(완료)' : (statusFilter.includes('canceled') || statusFilter.includes('deleted')) ? '(취소/삭제)' : '(진행중)'}</span>
          </h3>
          <p>
            사업장 내 유해·위험요인을 사전 발굴하고 개선대책을 수립/이행 관리하는 통합 안전 모니터링 시스템입니다.
          </p>
        </div>
        {canCreateProject && (
          <button onClick={handleOpenModal} style={{ background: '#3b82f6', color: '#ffffff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)', whiteSpace: 'nowrap' }}>
            <i className="fas fa-plus"></i> 신규 위험성평가 등록
          </button>
        )}
      </div>

      {/* KPI 통계 카드 4종 */}
      <div className="kpi-wrapper mb-4">
        {[
          { label: includeCompleted ? '총 평가 건수' : statusFilter === 'completed' ? '총 완료 평가' : (statusFilter.includes('canceled') || statusFilter.includes('deleted')) ? '총 취소/삭제 평가' : '총 진행 평가', value: totalCount, unit: '건', color: '#3b82f6', textColor: '#0f172a', badge: '실시간', badgeColor: '#dbeafe', badgeText: '#1d4ed8' },
          { label: '유해위험요인 발굴', value: stats.totalHazards, unit: '건', color: '#f59e0b', textColor: '#0f172a', badge: '누적 분석', badgeColor: '#fef3c7', badgeText: '#b45309' },
          { label: '개선 조치 이행률', value: stats.actionRate, unit: '%', color: '#10b981', textColor: '#10b981', badge: '양호', badgeColor: '#d1fae5', badgeText: '#047857' },
          { label: '고위험(상) 즉시개선', value: stats.highRiskImmediate, unit: '건', color: '#ef4444', textColor: '#ef4444', badge: '우선 관리', badgeColor: '#fee2e2', badgeText: '#b91c1c' },
        ].map((kpi, idx) => (
          <div className="kpi-box" key={idx} style={{ borderTop: `4px solid ${kpi.color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span style={{ color: '#64748b', fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 6 }}>{kpi.label}</span>
                <h3 style={{ fontWeight: 800, margin: 0, color: kpi.textColor }}>
                  {kpi.value}<span style={{ fontSize: '0.85rem', marginLeft: 4, color: kpi.textColor }}>{kpi.unit}</span>
                </h3>
              </div>
              <span style={{ background: kpi.badgeColor, color: kpi.badgeText, padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700 }}>
                {kpi.badge}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* 메인 데이터 카드 */}
      <div className="sf-card">
        {/* 컨트롤 패널 (탭 + 검색) */}
        <div className="sf-card-header" style={{ padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap', gap: '16px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: '12px' }}>
            <select
              value={filterYear}
              onChange={(e) => { 
                setFilterYear(e.target.value); 
                setCurrentPage(1); 
              }}
              style={{
                height: '38px',
                padding: '0 32px 0 12px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#475569',
                outline: 'none',
                backgroundColor: '#fff',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px top 50%',
                backgroundSize: '10px auto'
              }}
            >
              <option value="ALL">전체 연도</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}년</option>
              ))}
            </select>
            
            <div style={{ display: 'flex', background: '#e8edf3', padding: '4px', borderRadius: '10px', gap: 4 }}>
              {[
                { key: 'ALL', label: '전체', count: filteredForCount.length },
                { key: '정기', label: '정기 평가', count: filteredForCount.filter(p => p.type === '정기').length },
                { key: '수시', label: '수시 평가', count: filteredForCount.filter(p => p.type === '수시').length }
              ].map(tab => {
                const isActive = filterType === tab.key;
                const btnStyle = isActive
                  ? { background: '#ffffff', border: 'none', borderRadius: '8px', padding: '6px 20px', fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '8px' }
                  : { background: 'transparent', border: 'none', borderRadius: '8px', padding: '6px 20px', fontWeight: 600, transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '8px' };

                return (
                  <button
                    key={tab.key}
                    onClick={() => { setFilterType(tab.key); setCurrentPage(1); }}
                    style={btnStyle}
                  >
                    <span style={{ color: isActive ? '#003366' : '#336699', fontSize: '0.9rem' }}>{tab.label}</span>
                    <span style={{ color: isActive ? '#475569' : '#94a3b8', fontSize: '0.8rem' }}>{tab.count}건</span>
                  </button>
                );
              })}
            </div>

            {/* 완료 포함 토글 스위치 (진행중 평가 시에만 노출) */}
            {(statusFilter === 'in_progress' || !statusFilter) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '16px' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', gap: '8px', userSelect: 'none', margin: 0 }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="checkbox"
                      checked={includeCompleted}
                      onChange={(e) => { setIncludeCompleted(e.target.checked); setCurrentPage(1); }}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      width: '38px',
                      height: '20px',
                      backgroundColor: includeCompleted ? '#3b82f6' : '#cbd5e1',
                      borderRadius: '10px',
                      transition: 'background-color 0.2s',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: '14px',
                        height: '14px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '3px',
                        left: includeCompleted ? '21px' : '3px',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                      }} />
                    </div>
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: includeCompleted ? '#3b82f6' : '#64748b' }}>완료 포함</span>
                </label>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative', flexShrink: 1, minWidth: 0 }}>
            {/* --- 인라인 검색 결과 안내 (검색조건 Select 앞) --- */}
            {searchTerm && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '4px', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '4px 12px', borderRadius: '20px', flexShrink: 1, minWidth: 0 }}>
                <span style={{ fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 600, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                  '
                  <span style={{ color: '#2563eb', fontWeight: 700, maxWidth: '400px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                    {searchTerm}
                  </span>
                  ' 검색결과
                </span>
                <button
                  onClick={() => { setSearchType('ALL'); setSearchInput(''); setSearchTerm(''); setCurrentPage(1); }}
                  style={{ background: '#bfdbfe', border: 'none', borderRadius: '50%', fontSize: '0.7rem', color: '#1e3a8a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', padding: 0, flexShrink: 0 }}
                  title="검색 초기화"
                >
                  ✖
                </button>
              </div>
            )}
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              style={{ height: '38px', padding: '0 32px 0 12px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', outline: 'none', backgroundColor: '#fff', cursor: 'pointer', appearance: 'none', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px top 50%', backgroundSize: '10px auto', flexShrink: 0 }}
            >
              <option value="ALL">전체</option>
              <option value="NAME">프로젝트명</option>
              <option value="SITE">사업장명</option>
              <option value="MANAGER">관리감독자</option>
              <option value="STATUS">상태</option>
            </select>
            <input
              type="text"
              placeholder="검색어 입력"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setSearchTerm(searchInput);
                  setSearchInput('');
                  setCurrentPage(1);
                }
              }}
              style={{ width: '220px', height: '38px', padding: '0 12px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', backgroundColor: '#fff', flexShrink: 1, minWidth: '80px' }}
            />
            <button
              onClick={() => { setSearchTerm(searchInput); setSearchInput(''); setCurrentPage(1); }}
              style={{ height: '38px', padding: '0 16px', background: '#64748b', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              검색
            </button>
            <button
              onClick={() => { setSearchType('ALL'); setSearchInput(''); setSearchTerm(''); setCurrentPage(1); }}
              style={{ height: '38px', padding: '0 16px', background: 'white', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              초기화
            </button>

            {/* 뷰 모드 토글 (리스트 / 카드) */}
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '3px', marginLeft: '12px', border: '1px solid #e2e8f0', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => { setViewMode('list'); setCurrentPage(1); }}
                style={{
                  border: 'none',
                  background: viewMode === 'list' ? 'white' : 'transparent',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: viewMode === 'list' ? '#334155' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' : 'none',
                  transition: 'all 0.2s',
                  fontSize: '0.9rem'
                }}
                title="리스트 뷰"
              >
                <i className="fas fa-list"></i>
              </button>
              <button
                type="button"
                onClick={() => { setViewMode('card'); setCurrentPage(1); }}
                style={{
                  border: 'none',
                  background: viewMode === 'card' ? 'white' : 'transparent',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: viewMode === 'card' ? '#334155' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: viewMode === 'card' ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' : 'none',
                  transition: 'all 0.2s',
                  fontSize: '0.9rem'
                }}
                title="카드 뷰"
              >
                <i className="fas fa-th-large"></i>
              </button>
            </div>
          </div>
        </div>

        {/* 리스트 영역 (진행중은 카드, 그 외는 테이블) */}
        <div style={{ padding: 0, maxWidth: '100%' }}>
          {viewMode === 'card' ? (
            <div className="project-card-grid" style={{ display: 'grid', gap: '20px', padding: '24px' }}>
              <style>{`
                .project-card-grid {
                  grid-template-columns: repeat(4, minmax(0, 1fr));
                }
                @media (max-width: 1399px) {
                  .project-card-grid {
                    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
                  }
                }
                @media (max-width: 1024px) {
                  .project-card-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                  }
                }
                @media (max-width: 768px) {
                  .project-card-grid {
                    grid-template-columns: 1fr !important;
                  }
                }
                .project-card-hover {
                  background: #f8fafc;
                  border-radius: 12px;
                  padding: 10px 12px;
                  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                  border: 1px solid #e2e8f0;
                  cursor: pointer;
                  display: flex;
                  flex-direction: column;
                  gap: 6px;
                  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease !important;
                }
                .project-card-hover:hover {
                  transform: translateY(-3px) !important;
                  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
                  border-color: #cbd5e1 !important;
                }
              `}</style>
              {projects.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '36px 24px' }}>
                  <div className="sf-empty" style={{ padding: 0 }}>
                    <div className="sf-empty-icon" style={{ color: '#cbd5e1', marginBottom: '12px' }}>
                      {searchTerm ? <i className="fas fa-search"></i> : <i className="fas fa-folder-open"></i>}
                    </div>
                    <div className="sf-empty-text" style={{ fontSize: '0.92rem', color: '#64748b', fontWeight: 500, lineHeight: 1.6 }}>
                      {searchTerm ? (
                        '검색어와 일치하는 결과가 없습니다.'
                      ) : (
                        <>
                          등록된 위험성평가 프로젝트가 없습니다.<br />
                          <span style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 400 }}>
                            상단의 '완료 포함' 스위치를 켜거나, 아래 버튼을 눌러 새 프로젝트를 등록해 보세요.
                          </span>
                        </>
                      )}
                    </div>
                    {!searchTerm && canCreateProject && (
                      <button
                        onClick={handleOpenModal}
                        style={{
                          marginTop: '16px',
                          background: '#3b82f6',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '10px 20px',
                          fontWeight: 700,
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <i className="fas fa-plus"></i> 신규 위험성평가 등록
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                projects.map((proj) => {
                  const dateStr = proj.period ? proj.period.replace(/\./g, '-') : '';
                  const badgeClass = proj.type === '정기' ? 'sf-badge-primary' : 'sf-badge-danger';
                  
                  return (
                    <div key={proj.id} onClick={() => navigate(`/safety/projects/${proj.id}`)} className="project-card-hover">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', overflow: 'hidden', flexWrap: 'nowrap' }}>
                        <span className={`sf-badge ${badgeClass}`} style={{ fontSize: '0.72rem', padding: '2px 8px', lineHeight: '1.4', whiteSpace: 'nowrap', flexShrink: 0 }}>{proj.type}평가</span>
                        <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }} title={dateStr}>
                          <i className="far fa-calendar-alt me-1" style={{ flexShrink: 0 }}></i>{dateStr}
                        </span>
                        <button
                          className="sf-btn sf-btn-sm"
                          onClick={(e) => { e.stopPropagation(); handleOpenViewModal(proj, e); }}
                          style={{
                            marginLeft: 'auto',
                            borderRadius: 6,
                            height: 26,
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontWeight: 700,
                            background: '#ffffff',
                            color: '#2563eb',
                            border: '1px solid #bfdbfe',
                            padding: '0 10px',
                            fontSize: '0.75rem',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                            cursor: 'pointer',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          조회
                        </button>
                      </div>
                      <h4 style={{ margin: '0', fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={proj.name}>{proj.name}</h4>
                      
                      <div style={{ background: '#ffffff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                          <span style={{ color: '#64748b', fontWeight: 600 }}>평가건수</span>
                          <span style={{ color: '#1e40af', fontWeight: 800 }}>{proj.hazardCount || 0} 건</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                          <span style={{ color: '#64748b', fontWeight: 600 }}>참여인원</span>
                          <span style={{ color: '#0f172a', fontWeight: 700 }}>{proj.workerCnt ? `${proj.workerCnt}명` : '-'}</span>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px' }}>
                        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                          작성일: {(proj.createdAt || proj.created_at) ? (() => {
                            const d = new Date(proj.createdAt || proj.created_at);
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            return `${y}-${m}-${day}`;
                          })() : '-'}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {renderNamePositionBadge(proj.managerName, true)}
                          {proj.status === 'completed' && (
                            <span style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 800, padding: '2px 8px', whiteSpace: 'nowrap' }}>✔ 완료</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div className="table-responsive" style={{ width: '100%', overflowX: 'auto', display: 'block', WebkitOverflowScrolling: 'touch' }}>
              <table className="sf-table" style={{ fontSize: '0.88rem', minWidth: '1160px' }}>
                <thead>
                  <tr>
                    <th style={{ width: 50, minWidth: '50px', textAlign: 'center' }}>No</th>
                    <th style={{ width: 75, minWidth: '65px', textAlign: 'center' }}>상태</th>
                    <th style={{ textAlign: 'left', paddingLeft: '16px', minWidth: '250px' }}>평가명</th>
                    <th style={{ width: 70, minWidth: '60px', textAlign: 'center' }}>구분</th>
                    <th style={{ width: 105, minWidth: '95px', textAlign: 'center', whiteSpace: 'nowrap' }}>평가건수</th>
                    <th style={{ width: 200, minWidth: '160px', textAlign: 'center' }}>평가 기간</th>
                    <th style={{ width: 105, minWidth: '100px', textAlign: 'center', whiteSpace: 'nowrap' }}>참여인원</th>
                    <th style={{ width: 150, minWidth: '100px', textAlign: 'center' }}>관리감독자</th>
                    <th style={{ width: 120, minWidth: '95px', textAlign: 'center' }}>작성일</th>
                    <th style={{ width: 90, minWidth: '80px', textAlign: 'center' }}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '48px 24px' }}>
                        <div className="sf-empty" style={{ padding: 0 }}>
                          <div className="sf-empty-icon" style={{ color: '#cbd5e1', marginBottom: '12px' }}>
                            {searchTerm ? <i className="fas fa-search"></i> : <i className="fas fa-folder-open"></i>}
                          </div>
                          <div className="sf-empty-text" style={{ fontSize: '0.92rem', color: '#64748b', fontWeight: 500, lineHeight: 1.6 }}>
                            {searchTerm ? (
                              '검색어와 일치하는 결과가 없습니다.'
                            ) : (
                              <>
                                등록된 위험성평가 프로젝트가 없습니다.<br />
                                <span style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 400 }}>
                                  상단의 '완료 포함' 스위치를 켜거나, 아래 버튼을 눌러 새 프로젝트를 등록해 보세요.
                                </span>
                              </>
                            )}
                          </div>
                          {!searchTerm && (
                            <button
                              onClick={handleOpenModal}
                              style={{
                                marginTop: '16px',
                                background: '#3b82f6',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: 8,
                                padding: '10px 20px',
                                fontWeight: 700,
                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              <i className="fas fa-plus"></i> 신규 위험성평가 등록
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    projects.map((proj, idx) => (
                      <tr key={proj.id} onClick={() => navigate(`/safety/projects/${proj.id}`)} style={{ cursor: 'pointer' }}>
                        <td style={{ textAlign: 'center' }}>
                          <span className="sf-row-num">{totalCount - ((currentPage - 1) * itemsPerPage) - idx}</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {(() => {
                            const status = proj.status || 'in_progress';
                            const STATUS_LABEL = {
                              in_progress: '진행중',
                              completed: '완료',
                              canceled: '취소',
                              deleted: '삭제',
                            };
                            const badgeClass =
                              status === 'completed' ? 'sf-badge-success' :
                                status === 'canceled' ? 'sf-badge-secondary' :
                                  status === 'deleted' ? 'sf-badge-danger' : 'sf-badge-warning';

                            return (
                              <span className={`sf-badge ${badgeClass}`}
                                style={{ padding: '5px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}
                              >
                                {STATUS_LABEL[status]}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ verticalAlign: 'middle', maxWidth: '300px' }}>
                          <div title={proj.name} style={{ fontWeight: 700, color: 'var(--sf-text)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {proj.name}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`sf-badge ${proj.type === '정기' ? 'sf-badge-primary' : proj.type === '수시' ? 'sf-badge-danger' : 'sf-badge-accent'}`}
                            style={{ padding: '5px 14px', borderRadius: 20, whiteSpace: 'nowrap' }}
                          >
                            {proj.type}평가
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="sf-badge sf-badge-outline-primary" style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 800, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                            {proj.hazardCount || 0} 건
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--sf-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }} title={proj.period ? proj.period.replace(/\./g, '-') : ''}>{proj.period ? proj.period.replace(/\./g, '-') : ''}</td>
                        <td style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--sf-text-secondary)', whiteSpace: 'nowrap' }}>{proj.workerCnt ? `${proj.workerCnt}명` : '-'}</td>
                        <td style={{ textAlign: 'center' }}>
                          {renderNamePositionBadge(proj.managerName, true)}
                        </td>
                        <td style={{ textAlign: 'center', fontSize: '0.84rem', maxWidth: '95px' }}>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={(proj.createdAt || proj.created_at) ? (() => {
                            const d = new Date(proj.createdAt || proj.created_at);
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            return `${y}-${m}-${day}`;
                          })() : ''}>
                            {(proj.createdAt || proj.created_at) ? (() => {
                              const d = new Date(proj.createdAt || proj.created_at);
                              const y = d.getFullYear();
                              const m = String(d.getMonth() + 1).padStart(2, '0');
                              const day = String(d.getDate()).padStart(2, '0');
                              return `${y}-${m}-${day}`;
                            })() : '-'}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                            <button className="sf-btn sf-btn-sm" onClick={(e) => handleOpenViewModal(proj, e)} style={{ borderRadius: 6, height: 32, display: 'inline-flex', alignItems: 'center', fontWeight: 700, background: '#ffffff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '0 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                              조회
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 페이징 */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid var(--sf-border-light)', background: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
        }}>
          <div style={{
            position: 'absolute', left: '24px',
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            padding: '4px 12px', borderRadius: '20px',
            fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
          }}>
            <span style={{ color: totalCount === 0 ? '#e2e8f0' : '#2563eb' }}>{totalCount === 0 ? 0 : currentPage}</span>
            <span style={{ color: totalCount === 0 ? '#e2e8f0' : '#93c5fd', margin: '0 -2px' }}>/</span>
            <span>{totalCount === 0 ? 0 : Math.max(1, totalPages)}</span>
            <span style={{ color: totalCount === 0 ? '#e2e8f0' : '#bfdbfe', margin: '0 2px' }}>|</span>
            <span>총 {totalCount}건</span>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {(() => {
              const PAGE_BLOCK_SIZE = 10;
              const safeTotalPages = Math.max(1, totalPages);
              const startPage = Math.floor((currentPage - 1) / PAGE_BLOCK_SIZE) * PAGE_BLOCK_SIZE + 1;
              const endPage = Math.min(startPage + PAGE_BLOCK_SIZE - 1, safeTotalPages);
              const pagesInBlock = Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);

              return (
                <>
                  {/* 처음으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1 || totalCount === 0}
                    style={{
                      opacity: (currentPage === 1 || totalCount === 0) ? 0.4 : 1,
                      cursor: (currentPage === 1 || totalCount === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === 1 || totalCount === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="처음으로"
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                  {/* 이전 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.max(startPage - 1, 1))}
                    disabled={startPage === 1 || totalCount === 0}
                    style={{
                      opacity: (startPage === 1 || totalCount === 0) ? 0.4 : 1,
                      cursor: (startPage === 1 || totalCount === 0) ? 'default' : 'pointer',
                      pointerEvents: (startPage === 1 || totalCount === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="이전 10페이지"
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>

                  {pagesInBlock.map(page => (
                    <button
                      key={page}
                      className={`sf-btn sf-btn-sm ${currentPage === page && totalCount > 0 ? 'sf-btn-primary' : 'sf-btn-outline'}`}
                      onClick={() => setCurrentPage(page)}
                      disabled={totalCount === 0}
                      style={{
                        minWidth: 36, borderRadius: 8,
                        opacity: totalCount === 0 ? 0.5 : 1,
                        cursor: totalCount === 0 ? 'default' : 'pointer',
                        pointerEvents: totalCount === 0 ? 'none' : 'auto'
                      }}
                    >
                      {page}
                    </button>
                  ))}

                  {/* 다음 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.min(endPage + 1, safeTotalPages))}
                    disabled={endPage === safeTotalPages || totalCount === 0}
                    style={{
                      opacity: (endPage === safeTotalPages || totalCount === 0) ? 0.4 : 1,
                      cursor: (endPage === safeTotalPages || totalCount === 0) ? 'default' : 'pointer',
                      pointerEvents: (endPage === safeTotalPages || totalCount === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="다음 10페이지"
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                  {/* 마지막으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(safeTotalPages)}
                    disabled={currentPage === safeTotalPages || totalCount === 0}
                    style={{
                      opacity: (currentPage === safeTotalPages || totalCount === 0) ? 0.4 : 1,
                      cursor: (currentPage === safeTotalPages || totalCount === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === safeTotalPages || totalCount === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="마지막으로"
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* 위험성평가 프로젝트 모달 (등록/수정/조회) */}
      <SafetyProjectModal
        show={modalMode !== null}
        mode={modalMode || 'create'}
        initialData={modalData}
        token={token}
        hasPermission={modalMode === 'view' && modalData ? hasProjectPermission(modalData) : false}
        onClose={() => { setModalMode(null); setModalData(null); }}
        onSaved={handleModalSaved}
        onEdit={(proj) => { setModalMode('edit'); setModalData(proj); }}
      />
    </div>
  );
}

export default SafetyProjectList;
